// Commercial Acoustic Calculator — engine facade.
// Depends on engine-core.js and engine-solutions.js being loaded first.
// Exposes the top-level API used by commercial.js and by regression tests.

(function () {
  "use strict";

  const ns = window.AcousticCalculatorEngine;
  if (!ns || !ns.buildContext || !ns.calculateWallsFirst) {
    throw new Error(
      "AcousticCalculatorEngine core+solutions not loaded. Include engine-core.js and engine-solutions.js before engine.js.",
    );
  }
  const {
    BANDS,
    buildContext,
    calculateRT60,
    calculateExistingAbsorption,
    calculateWallsFirst,
    calculateCeilingFirst,
    calculateBalanced,
  } = ns;

  // Sheet rows 244-253: archetype log formulas compute base target from volume.
  // Each ROOM_TYPES entry maps to one of these via its `archetype` field.
  const ARCHETYPE_FORMULAS = {
    church_choral: function (v) {
      return Math.max(0.5, (0.44 * Math.log10(v / 1000) + 0.72) * 1.2);
    },
    concert_hall: function (v) {
      return Math.max(0.5, (0.44 * Math.log10(v / 1000) + 0.64) * 1.2);
    },
    church_speech: function (v) {
      return Math.max(0.5, (0.44 * Math.log10(v / 1000) + 0.52) * 1.2);
    },
    theater: function (v) {
      return Math.max(0.5, (0.305 * Math.log10(v / 1000) + 0.511) * 1.2);
    },
    theater_conf_avg: function (v) {
      return (
        (ARCHETYPE_FORMULAS.theater(v) + ARCHETYPE_FORMULAS.conf_lecture(v)) / 2
      );
    },
    conf_lecture: function (v) {
      return Math.max(0.5, (0.24 * Math.log10(v / 1000) + 0.443) * 1.2);
    },
    ansi_classroom: function (v) {
      return v < 10000 ? 0.6 : 0.7;
    },
    recording_studio: function (v) {
      return Math.max(0.3, (0.33 * Math.log10(v / 1000) + 0.17) * 1.2);
    },
  };

  // Sheet rows 257-308 column F: large-room target adjustment.
  // Depends on mean free path, ceiling height, and sound source type.
  function computeLargeRoomTarget(baseTarget, entry, ctx) {
    const totalSurfaceArea =
      ctx.wallArea + ctx.entrywayArea + ctx.ceilingArea * 2;
    const mfp =
      totalSurfaceArea > 0 ? (4 * ctx.roomVolume) / totalSurfaceArea : 0;
    const height = ns.toNum(ctx.inputs.room_height);
    const isLoud = entry.sound_source === "loud";

    if (mfp > 30 && !isLoud) {
      return ns.round4(baseTarget * 1.2);
    }
    if (height < 22 && isLoud) {
      const lower =
        entry.archetype === "recording_studio" ||
        entry.archetype === "ansi_classroom"
          ? 0.3
          : 0.5;
      return Math.max(lower, ns.round4(baseTarget * 0.8));
    }
    return baseTarget;
  }

  // Target RT60 from the sheet's archetype formulas.
  // Reads inputs.building + inputs.room, falls back to catalog.defaultBuildingRoom.
  // Volume above 50,000 ft³ applies the large-room RT60 adjustment.
  function computeTargetRT60(inputs, catalog) {
    const table = catalog.ROOM_TYPES || [];
    const defaults = catalog.defaultBuildingRoom || {};
    const building = inputs.building || defaults.building;
    const room = inputs.room || defaults.room;
    const entry = table.find((r) => r.building === building && r.room === room);
    if (!entry) {
      throw new Error(`Unknown room type: ${building} / ${room}`);
    }
    const formula = ARCHETYPE_FORMULAS[entry.archetype];
    if (!formula) {
      throw new Error(`Unknown archetype: ${entry.archetype}`);
    }
    const ctx = buildContext(inputs, catalog);
    const baseTarget = ns.round4(formula(ctx.roomVolume));
    const threshold =
      (catalog.thresholds && catalog.thresholds.large_room_volume_ft3) || 50000;
    if (ctx.roomVolume > threshold) {
      return computeLargeRoomTarget(baseTarget, entry, ctx);
    }
    return baseTarget;
  }

  // Untreated RT60 per frequency band. Matches the DevTools capture helper.
  function computeRT60PerBand(inputs, catalog) {
    const ctx = buildContext(inputs, catalog);
    const out = {};
    BANDS.forEach((frequency) => {
      const absorption = calculateExistingAbsorption(frequency, ctx);
      out[frequency] = calculateRT60(ctx.roomVolume, absorption);
    });
    return out;
  }

  // Wall-only qty required to close bands 500-4000 Hz for a given product.
  // Mirrors the sizing loop in calculateACTOnly but lets the caller pass an
  // explicit product (used for the 9mm-felt benchmark comparator).
  function wallOnlyQtyForProduct(product, targetRT60, ctx) {
    if (!product) return 0;
    const benchCtx = { ...ctx, wallProduct: product };
    let maxQ = 0;
    BANDS.forEach((frequency) => {
      if (frequency === "250 Hz") return;
      const q = ns.findQuantityToCloseBand(
        frequency,
        "wall",
        targetRT60,
        benchCtx,
      );
      if (q > maxQ) maxQ = q;
    });
    return maxQ;
  }

  // Sheet rows 129-131: 9mm felt benchmark target for ACT rooms.
  // B131 = ROUNDUP(0.5 * (B67 + B69)) — half of wall1 + wall3 effective area (sq ft)
  // Sheet rows 128-132: benchmark sizing.
  // B131 = ROUNDUP(0.5 * (wall1 + wall3)) — fixed geometric 9mm benchmark qty.
  // B132 = MAX(1, ROUNDUP(MAX(C128:F128))) — chosen product equivalent.
  function computeBenchmark9mm(inputs, catalog) {
    const ctx = buildContext(inputs, catalog);
    const effectiveTarget = computeTargetRT60(inputs, catalog);

    const wallProducts = (catalog.feltRightProducts || {}).wall || [];
    const felt9mm = wallProducts.find((p) => p.id === "9mm_felt_2x2");
    const wall1Area = ctx.wallSlots[0] ? ctx.wallSlots[0].effectiveArea : 0;
    const sheetWall3Area = ctx.wallSlots[2]
      ? ctx.wallSlots[2].effectiveArea
      : 0;
    const qty9mm = Math.ceil(0.5 * (wall1Area + sheetWall3Area));

    let qtyChosen = 0;
    if (ctx.wallProduct) {
      let maxQ = 0;
      BANDS.forEach((frequency) => {
        if (frequency === "250 Hz") return;
        const productAbs = ctx.wallProduct.absorption[frequency];
        if (!productAbs) return;
        const existing = calculateExistingAbsorption(frequency, ctx);
        const requiredUnits =
          (ns.RT60_CONSTANT * ctx.roomVolume) / (effectiveTarget * productAbs);
        const existingUnits = existing / productAbs;
        const q = Math.floor(Math.max(requiredUnits - existingUnits, 0));
        if (q > maxQ) maxQ = q;
      });
      qtyChosen = Math.max(1, maxQ);
    }

    return {
      target_rt60_sec: effectiveTarget,
      qty_needed_9mm: qty9mm,
      qty_needed_chosen: qtyChosen,
    };
  }

  // Contact-sales trigger: untreated RT60 already under target at 500-4000 Hz
  // means the room doesn't need Felt Right treatment — route to sales instead
  // of a solution. 250 Hz is excluded (rarely achievable without bass trapping
  // and consistent with the sizing loops' 250 Hz exclusion).
  function detectContactSalesReason(ctx, targetRT60) {
    const upper = ["500 Hz", "1000 Hz", "2000 Hz", "4000 Hz"];
    const allMet = upper.every((band) => {
      const absorption = calculateExistingAbsorption(band, ctx);
      const rt60 = calculateRT60(ctx.roomVolume, absorption);
      return rt60 <= targetRT60;
    });
    return allMet ? "target_already_met" : null;
  }

  // Sheet rows 115-118: linear interpolation between walls_first (slider=0)
  // and ceiling_first (slider=100). Returns a solution whose rt60ByFrequency
  // reflects the interpolated product counts, not an average of the endpoints.
  function computeSliderMix(sliderPct, inputs, catalog) {
    const pct = Math.max(0, Math.min(100, sliderPct)) / 100;
    const ctx = buildContext(inputs, catalog);
    const targetRT60 = computeTargetRT60(inputs, catalog);
    const wf = calculateWallsFirst(targetRT60, ctx);
    const cf = calculateCeilingFirst(targetRT60, ctx);

    const wallQuantity = Math.round(
      wf.wallQuantity * (1 - pct) + cf.wallQuantity * pct,
    );
    const ceilingQuantity = Math.round(
      wf.ceilingQuantity * (1 - pct) + cf.ceilingQuantity * pct,
    );

    const solution = ns.createEmptySolution();
    solution.wallQuantity = wallQuantity;
    solution.ceilingQuantity = ceilingQuantity;
    solution.wallSqFt =
      wallQuantity * ((ctx.wallProduct && ctx.wallProduct.footprint) || 0);
    solution.ceilingSqFt =
      ceilingQuantity *
      ((ctx.ceilingProduct && ctx.ceilingProduct.footprint) || 0);
    solution.wallNeeded = solution.wallSqFt;
    solution.ceilingNeeded = solution.ceilingSqFt;

    return ns.finalizeSolution(solution, targetRT60, ctx);
  }

  // Full three-tier solution set. Order matters: balanced averages walls+ceiling first.
  function buildSolutions(inputs, catalog) {
    const ctx = buildContext(inputs, catalog);
    const effectiveTarget = computeTargetRT60(inputs, catalog);

    const wallsFirst = calculateWallsFirst(effectiveTarget, ctx);
    const ceilingFirst = calculateCeilingFirst(effectiveTarget, ctx);
    const balanced = calculateBalanced(
      effectiveTarget,
      wallsFirst,
      ceilingFirst,
      ctx,
    );

    const out = {
      walls_first: wallsFirst,
      balanced,
      ceiling_first: ceilingFirst,
      benchmark_9mm: computeBenchmark9mm(inputs, catalog),
      effective_target_rt60: effectiveTarget,
    };

    const contactSales = detectContactSalesReason(ctx, effectiveTarget);
    if (contactSales) out.contact_sales_reason = contactSales;

    return out;
  }

  function stableSerialize(obj) {
    if (obj === null || obj === undefined) return "null";
    if (Array.isArray(obj))
      return "[" + obj.map(stableSerialize).join(",") + "]";
    if (typeof obj === "object") {
      var keys = Object.keys(obj).sort();
      return (
        "{" +
        keys
          .map(function (k) {
            return JSON.stringify(k) + ":" + stableSerialize(obj[k]);
          })
          .join(",") +
        "}"
      );
    }
    return JSON.stringify(obj);
  }

  function hashInputs(inputs) {
    var str = stableSerialize(inputs);
    var h = 0x811c9dc5;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    var hex = (h >>> 0).toString(16);
    while (hex.length < 8) hex = "0" + hex;
    return hex.slice(0, 6);
  }

  ns.computeTargetRT60 = computeTargetRT60;
  ns.computeRT60PerBand = computeRT60PerBand;
  ns.computeBenchmark9mm = computeBenchmark9mm;
  ns.computeSliderMix = computeSliderMix;
  ns.detectContactSalesReason = detectContactSalesReason;
  ns.buildSolutions = buildSolutions;
  ns.hashInputs = hashInputs;
  ns.stableSerialize = stableSerialize;
})();
